import { createElement, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Database as DbIcon, Plus, ChevronRight, Search } from 'lucide-react';
import { Container, PageHeader, Section, Toolbar, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, Stat, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Card,
  CardBody,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useMyServices, serviceLabel, serviceProviderScope } from '@/api/services';
import {
  DATABASE_SCOPE,
  useDatabases,
  useDatabase,
  useDatabaseSlaves,
  useCreateDatabase,
  useDeleteDatabase,
  type Database,
} from '@/api/db';
import { RowMenu } from '../compute/components/CrudBits';
import { useDebounce } from '../shared';
import { useSetContextualNav } from '@/components/layout/nav-context';

const BASE = '/services/database';

export function DatabasePage() {
  return (
    <Routes>
      <Route index element={<Picker />} />
      <Route path=":serviceId" element={<DbServiceShell />}>
        <Route index element={<DatabaseList />} />
        <Route path="create" element={<DatabaseCreate />} />
        <Route path=":dbId/*" element={<DatabaseDetail />} />
      </Route>
    </Routes>
  );
}

/** Registers the database service-scope sidebar, then renders the active route. */
function DbServiceShell() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `${BASE}/${serviceId}`;
  useSetContextualNav(
    () => ({
      sections: [
        {
          items: [
            {
              id: base,
              label: 'Databases',
              icon: createElement(DbIcon, { size: 16 }),
              onSelect: () => navigate(base),
            },
          ],
        },
      ],
      activeId: base,
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    }),
    [base, navigate],
  );
  return <Outlet />;
}

function tone(s?: unknown): StatusTone {
  const v = String(s ?? '').toLowerCase();
  if (v.includes('run') || v.includes('active') || v.includes('ready')) return 'success';
  if (v.includes('pend') || v.includes('creat') || v.includes('deploy')) return 'warning';
  if (v.includes('error') || v.includes('fail')) return 'danger';
  return 'muted';
}

function Picker() {
  const navigate = useNavigate();
  const { data: all = [], isLoading, isError } = useMyServices();
  const services = all.filter((s) => serviceProviderScope(s) === DATABASE_SCOPE);
  useEffect(() => {
    if (!isLoading && !isError && services.length === 1) navigate(`${BASE}/${services[0].id}`, { replace: true });
  }, [isLoading, isError, services, navigate]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<DbIcon size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href="/services" onClick={(e) => { e.preventDefault(); navigate('/services'); }}>Services</BreadcrumbItem>
            <BreadcrumbItem current>Database as a Service</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Database as a Service"
        description="Managed MySQL, PostgreSQL, MariaDB and Redis."
      />
      <Section title="Your database services" description="Pick a service to manage its databases.">
        {isLoading ? <Skeleton style={{ height: 116 }} /> : services.length === 0 ? (
          <EmptyState icon={<DbIcon size={28} />} title="No database services" description="You don't have any managed database services provisioned yet." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--vn-space-4)' }}>
            {services.map((svc) => (
              <button key={svc.id} type="button" style={card} onClick={() => navigate(`${BASE}/${svc.id}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-3)' }}>
                  <span style={iconWrap}><DbIcon size={20} /></span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{serviceLabel(svc)}</div>
                    <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{svc.provider?.display_name ?? 'Database'}</div>
                  </div>
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Open <ChevronRight size={14} /></span>
              </button>
            ))}
          </div>
        )}
      </Section>
    </Container>
  );
}

function DatabaseList() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: dbs = [], isLoading } = useDatabases(serviceId);
  const [del, setDel] = useState<Database | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400).trim().toLowerCase();
  const base = `${BASE}/${serviceId}`;

  const remove = useDeleteDatabase(serviceId!, { onSuccess: () => { show({ tone: 'success', title: 'Database deleting' }); setDel(null); }, onError: () => show({ tone: 'danger', title: 'Delete failed' }) });

  // Name filter runs in-memory: the database list is fully loaded, so search is
  // a pure presentation concern over the existing rows.
  const filtered = useMemo(
    () => (search ? dbs.filter((d) => String(d.name || d.id).toLowerCase().includes(search)) : dbs),
    [dbs, search],
  );

  const columns: ColumnDef<Database, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'engine', header: 'Engine', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{[row.original.engine || row.original.type, row.original.version].filter(Boolean).join(' ') || '—'}</span> },
    { id: 'status', header: 'Status', cell: ({ row }) => <StatusPill tone={tone(row.original.status || row.original.state)}>{row.original.status || row.original.state || 'Unknown'}</StatusPill> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'Manage', onSelect: () => navigate(`${base}/${row.original.id}`) }, { label: 'Delete', danger: true, onSelect: () => setDel(row.original) }]} /> },
  ];

  return (
    <Container size="xl">
      <PageHeader
        icon={<DbIcon size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href="/services" onClick={(e) => { e.preventDefault(); navigate('/services'); }}>Services</BreadcrumbItem>
            <BreadcrumbItem current>Databases</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Databases"
        description="Managed database instances."
        actions={<Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/create`)}>Create database</Button>}
      />
      {isLoading ? <Skeleton style={{ height: 200 }} /> : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Find databases by name"
                startSlot={<Search />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            }
          />
          <DataTable
            columns={columns}
            data={filtered}
            getRowId={(d) => String(d.id)}
            onRowClick={(d) => navigate(`${base}/${d.id}`)}
            emptyState={
              dbs.length === 0 ? (
                <EmptyState
                  icon={<DbIcon size={28} />}
                  title="No databases yet"
                  description="Create your first managed database."
                  actions={<Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/create`)}>Create database</Button>}
                />
              ) : (
                'No databases match your search.'
              )
            }
            footer={`Showing ${filtered.length} of ${dbs.length}`}
          />
        </>
      )}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete database?" description={`Permanently delete “${del?.name || del?.id}”?`} confirmLabel="Delete" destructive requireText={del?.name || del?.id} loading={remove.isPending} onConfirm={() => del && remove.mutate(del.id)} />
    </Container>
  );
}

const ENGINES = ['mysql', 'postgresql', 'mariadb', 'redis'];
const VERSIONS: Record<string, string[]> = { mysql: ['8.0', '5.7'], postgresql: ['16', '15', '14'], mariadb: ['11', '10.11'], redis: ['7', '6'] };

const createSchema = yup.object({
  name: yup.string().trim().required('Name is required.'),
  engine: yup.string().required(),
  version: yup.string().required(),
  flavor: yup.string().trim().required('A flavor is required.'),
});
type CreateForm = yup.InferType<typeof createSchema>;

function DatabaseCreate() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const base = `${BASE}/${serviceId}`;
  const { control, handleSubmit, watch, formState: { errors } } = useForm<CreateForm>({
    resolver: yupResolver(createSchema) as Resolver<CreateForm>,
    defaultValues: { name: '', engine: 'mysql', version: '8.0', flavor: 'v2g-standard-2-4' },
  });
  const engine = watch('engine');
  const create = useCreateDatabase(serviceId!, {
    onSuccess: () => { show({ tone: 'success', title: 'Database creation started' }); navigate(base); },
    onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Create failed' }),
  });

  return (
    <Container size="lg">
      <PageHeader
        icon={<DbIcon size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={base} onClick={(e) => { e.preventDefault(); navigate(base); }}>Databases</BreadcrumbItem>
            <BreadcrumbItem current>Create</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Create database"
      />
      <form onSubmit={handleSubmit((f) => create.mutate({ ...f, name: f.name.trim() }))} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section title="Database" style={{ marginBottom: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)', maxWidth: 640 }}>
            <Controller control={control} name="name" render={({ field }) => <Field label="Name" required error={errors.name?.message}>{(p) => <Input {...p} {...field} placeholder="app-db" invalid={!!errors.name} />}</Field>} />
            <Controller control={control} name="engine" render={({ field }) => (
              <Field label="Engine">{() => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ENGINES.map((e) => <SelectItem key={e} value={e} style={{ textTransform: 'capitalize' }}>{e}</SelectItem>)}</SelectContent>
                </Select>
              )}</Field>
            )} />
            <Controller control={control} name="version" render={({ field }) => (
              <Field label="Version">{() => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(VERSIONS[engine] ?? []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              )}</Field>
            )} />
            <Controller control={control} name="flavor" render={({ field }) => <Field label="Flavor" required error={errors.flavor?.message}>{(p) => <Input {...p} {...field} placeholder="v2g-standard-2-4" invalid={!!errors.flavor} />}</Field>} />
          </div>
        </Section>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={() => navigate(base)}>Cancel</Button>
          <Button type="submit" startIcon={<DbIcon size={15} />} disabled={create.isPending}>Create database</Button>
        </div>
      </form>
    </Container>
  );
}

const DB_TABS = [{ slug: '', label: 'Overview' }, { slug: 'slaves', label: 'Replicas' }];

function DatabaseDetail() {
  const navigate = useNavigate();
  const { serviceId, dbId } = useParams<{ serviceId: string; dbId: string }>();
  const { data: db, isLoading } = useDatabase(serviceId, dbId);
  const base = `${BASE}/${serviceId}/${dbId}`;
  const listPath = `${BASE}/${serviceId}`;

  if (isLoading) return <Container size="xl"><Skeleton style={{ height: 36, width: 240, marginBottom: 'var(--vn-space-4)' }} /><Skeleton style={{ height: 240 }} /></Container>;
  if (!db) return <Container size="xl"><EmptyState icon={<DbIcon size={28} />} title="Database not found" description="It may have been deleted." actions={<Button variant="secondary" onClick={() => navigate(listPath)}>Back</Button>} /></Container>;

  return (
    <Container size="xl">
      <PageHeader
        icon={<DbIcon size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={listPath} onClick={(e) => { e.preventDefault(); navigate(listPath); }}>Databases</BreadcrumbItem>
            <BreadcrumbItem current>{db.name || dbId}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={db.name || dbId || ''}
        actions={<StatusPill tone={tone(db.status || db.state)}>{db.status || db.state || 'Unknown'}</StatusPill>}
      />
      <DbTabs base={base} />
      <Routes>
        <Route index element={<DbOverview db={db} />} />
        <Route path="slaves" element={<DbSlaves serviceId={serviceId!} dbId={dbId!} />} />
        <Route path="*" element={<DbOverview db={db} />} />
      </Routes>
    </Container>
  );
}

function DbOverview({ db }: { db: Database }) {
  return (
    <Section title="Connection">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--vn-space-3)' }}>
        <Card flat><CardBody><Stat label="Engine" value={[db.engine || db.type, db.version].filter(Boolean).join(' ') || '—'} /></CardBody></Card>
        <Card flat><CardBody><Stat label="Endpoint" value={<span style={{ fontSize: 'var(--vn-font-size-sm)', wordBreak: 'break-all' }}>{db.endpoint || db.ip || '—'}</span>} /></CardBody></Card>
        <Card flat><CardBody><Stat label="Status" value={db.status || db.state || '—'} /></CardBody></Card>
      </div>
    </Section>
  );
}

function DbSlaves({ serviceId, dbId }: { serviceId: string; dbId: string }) {
  const { data: slaves = [], isLoading } = useDatabaseSlaves(serviceId, dbId);
  const columns: ColumnDef<Database, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'status', header: 'Status', cell: ({ row }) => <StatusPill tone={tone(row.original.status || row.original.state)}>{row.original.status || row.original.state || 'Unknown'}</StatusPill> },
    { id: 'endpoint', header: 'Endpoint', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.endpoint || row.original.ip || '—'}</span> },
  ];
  return (
    <Section title="Read replicas" description="Read-only copies of this database.">
      {isLoading ? null : (
        <DataTable
          columns={columns}
          data={slaves}
          getRowId={(s) => String(s.id)}
          emptyState={<EmptyState size="sm" icon={<DbIcon size={28} />} title="No replicas" description="Add a read replica to scale reads." />}
        />
      )}
    </Section>
  );
}

function DbTabs({ base }: { base: string }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Derive active tab slug from the current pathname.
  const activeSlug = DB_TABS.slice(1).find((t) => pathname.startsWith(`${base}/${t.slug}`))?.slug ?? '';
  return (
    <Tabs value={activeSlug} onValueChange={(v) => navigate(v ? `${base}/${v}` : base)} style={{ marginBottom: 'var(--vn-space-5)' }}>
      <TabsList>
        {DB_TABS.map((t) => (
          <TabsTrigger key={t.slug || 'overview'} value={t.slug}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

const card: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--vn-fg-default)', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-lg)', background: 'var(--vn-bg-surface)', padding: 'var(--vn-space-4)' };
const iconWrap: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 'var(--vn-radius-md)', background: 'var(--vn-bg-muted, var(--vn-bg-hover))', flexShrink: 0 };

export default DatabasePage;
